// 产品推荐功能 - Others Also Bought
class ProductAlsoBought extends HTMLElement {
  constructor() {
    super();
    this.carousel = this;
    this.isLoading = false;
    this.hasInitialized = false;
    this.carouselListenersAttached = false; // 防止重复添加轮播监听器
    this.resizeHandler = null; // 保存resize处理器引用
  }
  
  connectedCallback() {
    // 防止重复初始化
    if (this.hasInitialized) {
      return;
    }
    
    this.hasInitialized = true;
    
    // 检查是否是动态推荐
    if (this.dataset.isRecommendation === 'true') {
      this.loadRecommendations();
    } else {
      // 检查手动配置的推荐是否有产品
      this.checkInitialContent();
      this.initCarousel();
    }
  }
  
  disconnectedCallback() {
    // 清理事件监听器，防止内存泄漏
    console.log('Cleaning up product recommendations listeners');
    
    // 清理resize监听器
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    
    // 重置标记
    this.carouselListenersAttached = false;
  }
  
  async loadRecommendations() {
    const url = this.dataset.recommendationsUrl;
    if (!url) {
      console.error('No recommendations URL provided');
      return;
    }
    
    // 防止重复加载
    if (this.isLoading) {
      console.log('Already loading recommendations, skipping...');
      return;
    }
    
    // 防止已经加载过
    if (this.dataset.loaded === 'true') {
      console.log('Recommendations already loaded, skipping...');
      return;
    }
    
    this.isLoading = true;
    
    try {
      console.log('Loading recommendations from:', url);
      const response = await fetch(url);
      const html = await response.text();
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      const recommendationsContent = tempDiv.querySelector('.product-also-bought');
      if (recommendationsContent) {
        const slides = recommendationsContent.querySelectorAll('.product-also-bought__slide');
        const hasProducts = slides && slides.length > 0;
        
        console.log('Recommendations found:', hasProducts ? slides.length + ' products' : 'no products');
        
        if (hasProducts) {
          this.classList.remove('hidden-completely');
          this.innerHTML = recommendationsContent.innerHTML;
          console.log('Recommendations loaded, initializing carousel...');
          
          this.dataset.loaded = 'true';
          
          // DOM 被替换了，需要重置轮播监听器标记
          this.carouselListenersAttached = false;
          
          setTimeout(() => {
            this.initCarousel();
            this.isLoading = false;
          }, 100);
        } else {
          console.log('No recommendations found, hiding the entire section');
          this.classList.add('hidden-completely');
          this.innerHTML = '';
          this.dataset.loaded = 'true';
          this.isLoading = false;
        }
      } else {
        console.log('No recommendations content found, hiding section');
        this.classList.add('hidden-completely');
        this.innerHTML = '';
        this.dataset.loaded = 'true';
        this.isLoading = false;
      }
    } catch (error) {
      console.error('Failed to load recommendations:', error);
      this.dataset.loaded = 'true';
      this.isLoading = false;
    }
  }
  
  checkInitialContent() {
    const slides = this.querySelectorAll('.product-also-bought__slide');
    const emptyDiv = this.querySelector('.product-also-bought__empty');
    
    if (slides.length === 0 && emptyDiv && emptyDiv.textContent.trim() !== 'Please select products in block settings') {
      console.log('No initial products found, hiding section');
      this.classList.add('hidden-completely');
      this.innerHTML = '';
    } else if (slides.length > 0) {
      this.classList.remove('hidden-completely');
      console.log('Found', slides.length, 'initial products');
    }
  }
  
  initCarousel() {
    this.track = this.querySelector('.product-also-bought__track');
    this.prevBtn = this.querySelector('.carousel-btn--prev');
    this.nextBtn = this.querySelector('.carousel-btn--next');
    this.slides = Array.from(this.querySelectorAll('.product-also-bought__slide'));
    
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    
    this.init();
  }

  getSlidesPerView() {
    if (window.innerWidth <= 480) {
      return 2;
    } else if (window.innerWidth <= 768) {
      return 2;
    }
    return 3;
  }

  init() {
    this.setupCarousel();
    this.setupVariantSelectors();
    this.setupCardClickEvents();
    this.setupAddToCartButtons();
  }

  setupCarousel() {
    if (!this.track || this.slides.length === 0) {
      console.log('Carousel setup failed: missing track or slides');
      return;
    }
    
    // 防止重复添加监听器
    if (this.carouselListenersAttached) {
      console.log('Carousel listeners already attached, just updating...');
      this.updateCarousel();
      return;
    }
    
    if (!this.prevBtn || !this.nextBtn) {
      this.prevBtn = this.querySelector('.carousel-btn--prev');
      this.nextBtn = this.querySelector('.carousel-btn--next');
    }
    
    console.log('Setting up carousel with', this.slides.length, 'slides');
    
    this.currentSlide = 0;
    this.track.style.transform = 'translateX(0%)';
    
    if (this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Prev button clicked, current slide:', this.currentSlide);
        if (this.currentSlide > 0) {
          this.currentSlide--;
          this.updateCarousel();
        }
      });

      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const slidesPerView = this.getSlidesPerView();
        const maxSlide = Math.max(0, this.totalSlides - slidesPerView);
        console.log('Next button clicked, current slide:', this.currentSlide, 'max:', maxSlide);
        if (this.currentSlide < maxSlide) {
          this.currentSlide++;
          this.updateCarousel();
        }
      });
    }
    
    this.setupTouchEvents();
    
    // 窗口大小改变时重新计算（只添加一次）
    if (!this.resizeHandler) {
      this.resizeHandler = () => {
        this.updateCarousel();
      };
      window.addEventListener('resize', this.resizeHandler);
    }
    
    this.carouselListenersAttached = true;
    this.updateCarousel();
  }

  updateCarousel() {
    const slidesPerView = this.getSlidesPerView();
    const slideWidth = 100 / slidesPerView;
    const translateX = -(this.currentSlide * slideWidth);
    
    console.log('Updating carousel: slide', this.currentSlide, 'of', this.totalSlides, 'slidesPerView:', slidesPerView, 'translateX:', translateX);
    
    this.track.style.transform = `translateX(${translateX}%)`;
    this.track.setAttribute('data-current-slide', this.currentSlide);
    
    const maxSlide = Math.max(0, this.totalSlides - slidesPerView);
    
    if (this.prevBtn && this.nextBtn) {
      this.prevBtn.disabled = this.currentSlide === 0;
      this.nextBtn.disabled = this.currentSlide >= maxSlide;
      
      this.prevBtn.style.opacity = this.currentSlide === 0 ? '0.3' : '1';
      this.nextBtn.style.opacity = this.currentSlide >= maxSlide ? '0.3' : '1';
    }
  }

  setupTouchEvents() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (e) => {
      const target = e.target;
      
      // 排除交互元素
      if (this.isInteractiveElement(target)) {
        return;
      }
      
      // 如果点击的是可点击卡片或其内部元素，不触发轮播滑动
      const clickableCard = target.closest('.product-card--clickable');
      if (clickableCard) {
        return;
      }
      
      startX = e.touches[0].clientX;
      isDragging = true;
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      
      const diff = startX - currentX;
      const threshold = 50;
      
      if (Math.abs(diff) > threshold) {
        const slidesPerView = this.getSlidesPerView();
        const maxSlide = Math.max(0, this.totalSlides - slidesPerView);
        
        if (diff > 0 && this.currentSlide < maxSlide) {
          this.currentSlide++;
          this.updateCarousel();
        } else if (diff < 0 && this.currentSlide > 0) {
          this.currentSlide--;
          this.updateCarousel();
        }
      }
      
      isDragging = false;
    });
  }

  isInteractiveElement(element) {
    const interactiveElements = [
      'button',
      'select', 
      'input',
      'a',
      'textarea'
    ];
    
    const interactiveClasses = [
      'product-card__add-btn',
      'variant-selector'
    ];
    
    if (interactiveElements.includes(element.tagName.toLowerCase())) {
      return true;
    }
    
    for (let className of interactiveClasses) {
      if (element.classList.contains(className)) {
        return true;
      }
    }
    
    let parent = element.parentElement;
    for (let i = 0; i < 3 && parent; i++) {
      if (interactiveElements.includes(parent.tagName.toLowerCase())) {
        return true;
      }
      for (let className of interactiveClasses) {
        if (parent.classList.contains(className)) {
          return true;
        }
      }
      parent = parent.parentElement;
    }
    
    return false;
  }

  setupVariantSelectors() {
    const selectors = this.querySelectorAll('.variant-selector');
    
    selectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const variantId = selectedOption.value;
        const price = selectedOption.dataset.price;
        const comparePrice = selectedOption.dataset.comparePrice;
        const variantImage = selectedOption.dataset.image;
        
        const card = e.target.closest('.product-card');
        const variantInput = card.querySelector('.variant-id-input');
        const addButton = card.querySelector('.product-card__add-btn');
        const priceContainer = card.querySelector('.product-card__price');
        const imageElement = card.querySelector('.variant-image');
        
        if (variantInput) {
          variantInput.value = variantId;
        }
        
        if (imageElement) {
          const defaultSrc = imageElement.dataset.defaultSrc;
          const newSrc = variantImage || defaultSrc;
          
          if (newSrc !== imageElement.src) {
            imageElement.classList.add('loading');
            imageElement.onload = () => {
              imageElement.classList.remove('loading');
            };
            imageElement.src = newSrc;
          }
        }
        
        this.updatePrice(priceContainer, price, comparePrice);
        
        const isAvailable = !selectedOption.disabled;
        addButton.disabled = !isAvailable;
        const btnText = addButton.querySelector('.add-btn-text');
        if (btnText) {
          btnText.textContent = isAvailable ? '+ ADD' : 'Out of Stock';
        } else {
          addButton.textContent = isAvailable ? '+ ADD' : 'Out of Stock';
        }
      });
    });
  }

  updatePrice(container, price, comparePrice) {
    const formattedPrice = this.formatMoney(price);
    let priceHTML = `<span class="price-current">${formattedPrice}</span>`;
    
    if (comparePrice && comparePrice > price) {
      const formattedComparePrice = this.formatMoney(comparePrice);
      priceHTML = `<span class="price-compare">${formattedComparePrice}</span>` + priceHTML;
    }
    
    container.innerHTML = priceHTML;
  }

  formatMoney(cents) {
    const amount = parseFloat(cents) / 100;
    return `$${amount.toFixed(2)}`;
  }

  setupCardClickEvents() {
    const cards = this.querySelectorAll('.product-card--clickable');
    
    cards.forEach(card => {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;
      let isTouchMove = false;
      
      // 触摸开始
      card.addEventListener('touchstart', (e) => {
        // 如果点击的是交互元素，不处理
        if (this.isInteractiveElementForClick(e.target)) {
          return;
        }
        
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        isTouchMove = false;
      }, { passive: true });
      
      // 触摸移动
      card.addEventListener('touchmove', (e) => {
        if (!touchStartTime) return;
        
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const deltaX = Math.abs(touchX - touchStartX);
        const deltaY = Math.abs(touchY - touchStartY);
        
        // 如果移动距离超过10px，认为是滑动
        if (deltaX > 10 || deltaY > 10) {
          isTouchMove = true;
        }
      }, { passive: true });
      
      // 触摸结束
      card.addEventListener('touchend', (e) => {
        if (!touchStartTime) return;
        
        const touchDuration = Date.now() - touchStartTime;
        
        // 如果是点击（没有移动，且时间短）
        if (!isTouchMove && touchDuration < 500) {
          // 如果点击的是交互元素，不触发卡片点击
          if (this.isInteractiveElementForClick(e.target)) {
            touchStartTime = 0;
            return;
          }
          
          // 阻止默认行为和事件冒泡
          e.preventDefault();
          e.stopPropagation();
          this.triggerCardAction(card);
        }
        
        touchStartTime = 0;
        isTouchMove = false;
      }, { passive: false });
      
      // PC端点击事件
      card.addEventListener('click', (e) => {
        // 如果点击的是交互元素，不触发卡片点击
        if (this.isInteractiveElementForClick(e.target)) {
          return;
        }
        
        this.triggerCardAction(card);
      });
      
      // 改善卡片焦点可访问性
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      
      // 键盘事件支持
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.triggerCardAction(card);
        }
      });
    });
  }
  
  triggerCardAction(card) {
    // 查找卡片中的按钮
    const modalOpener = card.querySelector('modal-opener button');
    const addButton = card.querySelector('.product-card__add-btn');
    
    // 显示loading效果
    if (addButton) {
      addButton.classList.add('loading');
      const spinner = addButton.querySelector('.loading-overlay__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    }
    
    // 如果有弹窗按钮，触发弹窗
    if (modalOpener) {
      modalOpener.click();
      // 弹窗打开后移除loading
      setTimeout(() => {
        if (addButton) {
          addButton.classList.remove('loading');
          const spinner = addButton.querySelector('.loading-overlay__spinner');
          if (spinner) {
            spinner.classList.add('hidden');
          }
        }
      }, 500);
    } 
    // 如果有直接添加按钮，触发表单提交
    else if (addButton && addButton.type === 'submit') {
      addButton.click();
    }
  }

  isInteractiveElementForClick(element) {
    // 检查是否是按钮、选择器等交互元素
    const interactiveTags = ['BUTTON', 'SELECT', 'INPUT', 'A', 'TEXTAREA'];
    
    if (interactiveTags.includes(element.tagName)) {
      return true;
    }
    
    // 检查是否在交互元素内部
    const interactiveClasses = [
      'product-card__add-btn',
      'variant-selector',
      'product-card__variants',
      'loading-overlay__spinner'
    ];
    
    for (let className of interactiveClasses) {
      if (element.classList.contains(className) || element.closest('.' + className)) {
        return true;
      }
    }
    
    return false;
  }

  setupAddToCartButtons() {
    // 表单提交的loading效果
    const forms = this.querySelectorAll('form[data-type="add-to-cart-form"]');
    
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const button = form.querySelector('.product-card__add-btn');
        const spinner = button.querySelector('.loading-overlay__spinner');
        
        if (button && spinner) {
          button.classList.add('loading');
          spinner.classList.remove('hidden');
          
          // 超时保护
          setTimeout(() => {
            button.classList.remove('loading');
            spinner.classList.add('hidden');
          }, 3000);
        }
      });
    });
    
    // modal-opener按钮的loading效果
    const modalOpeners = this.querySelectorAll('modal-opener');
    
    modalOpeners.forEach(opener => {
      const button = opener.querySelector('button');
      if (button) {
        button.addEventListener('click', (e) => {
          // 防止冒泡到卡片点击
          e.stopPropagation();
          
          const spinner = button.querySelector('.loading-overlay__spinner');
          if (spinner) {
            button.classList.add('loading');
            spinner.classList.remove('hidden');
            
            // 弹窗打开后移除loading
            setTimeout(() => {
              button.classList.remove('loading');
              spinner.classList.add('hidden');
            }, 500);
          }
        });
      }
    });
    
    // 监听加入购物车成功事件
    document.addEventListener('cart:item_added', () => {
      const buttons = this.querySelectorAll('.product-card__add-btn.loading');
      buttons.forEach(button => {
        button.classList.remove('loading');
        const spinner = button.querySelector('.loading-overlay__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      });
    });
  }
}

// 注册自定义元素
customElements.define('product-also-bought', ProductAlsoBought);

// 直接初始化
function initProductAlsoBought() {
  const containers = document.querySelectorAll('.product-also-bought');
  console.log('Direct init: found', containers.length, 'containers');
  
  containers.forEach(container => {
    const track = container.querySelector('.product-also-bought__track');
    const prevBtn = container.querySelector('.carousel-btn--prev');
    const nextBtn = container.querySelector('.carousel-btn--next');
    const slides = container.querySelectorAll('.product-also-bought__slide');
    
    if (!track || !prevBtn || !nextBtn || slides.length === 0) {
      console.log('Missing elements for container:', {
        track: !!track,
        prevBtn: !!prevBtn,
        nextBtn: !!nextBtn,
        slides: slides.length
      });
      return;
    }
    
    let currentSlide = 0;
    const totalSlides = slides.length;
    
    function getSlidesPerView() {
      if (window.innerWidth <= 480) return 2;
      if (window.innerWidth <= 768) return 2;
      return 3;
    }
    
    function updateCarousel() {
      const slidesPerView = getSlidesPerView();
      const slideWidth = 100 / slidesPerView;
      const translateX = -(currentSlide * slideWidth);
      
      console.log('Direct update:', currentSlide, 'of', totalSlides, 'translateX:', translateX);
      
      track.style.transform = `translateX(${translateX}%)`;
      
      const maxSlide = Math.max(0, totalSlides - slidesPerView);
      prevBtn.disabled = currentSlide === 0;
      nextBtn.disabled = currentSlide >= maxSlide;
      prevBtn.style.opacity = currentSlide === 0 ? '0.3' : '1';
      nextBtn.style.opacity = currentSlide >= maxSlide ? '0.3' : '1';
    }
    
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('Direct prev clicked');
      if (currentSlide > 0) {
        currentSlide--;
        updateCarousel();
      }
    });
    
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('Direct next clicked');
      const slidesPerView = getSlidesPerView();
      const maxSlide = Math.max(0, totalSlides - slidesPerView);
      if (currentSlide < maxSlide) {
        currentSlide++;
        updateCarousel();
      }
    });
    
    function setupTouchEvents() {
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      track.addEventListener('touchstart', (e) => {
        const target = e.target;
        
        // 排除交互元素
        if (isInteractiveElement(target)) {
          return;
        }
        
        // 如果点击的是可点击卡片或其内部元素，不触发轮播滑动
        const clickableCard = target.closest('.product-card--clickable');
        if (clickableCard) {
          return;
        }
        
        startX = e.touches[0].clientX;
        isDragging = true;
      }, { passive: true });

      track.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
      }, { passive: true });

      track.addEventListener('touchend', () => {
        if (!isDragging) return;
        
        const diff = startX - currentX;
        const threshold = 50;
        
        if (Math.abs(diff) > threshold) {
          const slidesPerView = getSlidesPerView();
          const maxSlide = Math.max(0, totalSlides - slidesPerView);
          
          if (diff > 0 && currentSlide < maxSlide) {
            currentSlide++;
            updateCarousel();
          } else if (diff < 0 && currentSlide > 0) {
            currentSlide--;
            updateCarousel();
          }
        }
        
        isDragging = false;
      });
    }

    function isInteractiveElement(element) {
      const interactiveElements = [
        'button',
        'select', 
        'input',
        'a',
        'textarea'
      ];
      
      const interactiveClasses = [
        'product-card__add-btn',
        'variant-selector'
      ];
      
      if (interactiveElements.includes(element.tagName.toLowerCase())) {
        return true;
      }
      
      for (let className of interactiveClasses) {
        if (element.classList.contains(className)) {
          return true;
        }
      }
      
      let parent = element.parentElement;
      for (let i = 0; i < 3 && parent; i++) {
        if (interactiveElements.includes(parent.tagName.toLowerCase())) {
          return true;
        }
        for (let className of interactiveClasses) {
          if (parent.classList.contains(className)) {
            return true;
          }
        }
        parent = parent.parentElement;
      }
      
      return false;
    }

    function setupVariantSelectors() {
      const selectors = container.querySelectorAll('.variant-selector');
      
      selectors.forEach(selector => {
        selector.addEventListener('change', (e) => {
          const selectedOption = e.target.options[e.target.selectedIndex];
          const variantId = selectedOption.value;
          const variantImage = selectedOption.dataset.image;
          
          const card = e.target.closest('.product-card');
          const variantInput = card.querySelector('.variant-id-input');
          const addButton = card.querySelector('.product-card__add-btn');
          const imageElement = card.querySelector('.variant-image');
          
          if (variantInput) {
            variantInput.value = variantId;
          }
          
          if (imageElement) {
            const defaultSrc = imageElement.dataset.defaultSrc;
            const newSrc = variantImage || defaultSrc;
            
            if (newSrc !== imageElement.src) {
              imageElement.classList.add('loading');
              imageElement.onload = () => {
                imageElement.classList.remove('loading');
              };
              imageElement.src = newSrc;
            }
          }
          
          const isAvailable = !selectedOption.disabled;
          addButton.disabled = !isAvailable;
          addButton.textContent = isAvailable ? '+ ADD' : 'Out of Stock';
          
          console.log('Variant changed to:', variantId, 'Available:', isAvailable, 'Image:', variantImage || 'default');
        });
      });
    }

    setupVariantSelectors();
    setupTouchEvents();
    window.addEventListener('resize', updateCarousel);
    updateCarousel();
    
    console.log('Direct init completed for container. Add buttons:', container.querySelectorAll('.product-card__add-btn').length);
  });
}

// 注释掉手动初始化，使用 Web Component 自动初始化即可
// document.addEventListener('DOMContentLoaded', function() {
//   console.log('DOM loaded, starting product also bought init');
//   
//   setTimeout(() => {
//     initProductAlsoBought();
//   }, 100);
//   
//   setTimeout(() => {
//     const containers = document.querySelectorAll('.product-also-bought');
//     if (containers.length === 0) {
//       console.log('No containers found, retrying...');
//       setTimeout(initProductAlsoBought, 500);
//     }
//   }, 500);
// });

// if (window.Shopify && window.Shopify.theme) {
//   document.addEventListener('shopify:section:load', () => {
//     setTimeout(initProductAlsoBought, 100);
//   });
// }

